/**
 * Pine-style custom script runner: user code receives `candles` and `helpers`,
 * and must return `{ pane: 'price' | 'oscillator', series: [{ name, data, color? }] }`
 * or a single array of `{ time, value }` (plotted on the price pane).
 *
 * Runs in a disposable Web Worker with browser network APIs disabled. The worker cannot access
 * the page DOM or localStorage; a timeout terminates scripts that do not finish.
 */

function smaPoints(candles, period) {
  if (!candles?.length || period < 1) return [];
  let rollingSum = 0;
  return candles.flatMap((candle, index) => {
    rollingSum += candle.close;
    if (index >= period) {
      rollingSum -= candles[index - period].close;
    }
    if (index < period - 1) return [];
    return [{ time: candle.time, value: rollingSum / period }];
  });
}

function emaPoints(candles, period) {
  if (!candles?.length || period < 1) return [];
  const smoothing = 2 / (period + 1);
  let emaValue = candles[0].close;
  return candles.map((candle, index) => {
    if (index === 0) {
      return { time: candle.time, value: emaValue };
    }
    emaValue = candle.close * smoothing + emaValue * (1 - smoothing);
    return { time: candle.time, value: emaValue };
  });
}

function wmaPoints(candles, period) {
  if (!candles?.length || period < 1) return [];
  const weightSum = (period * (period + 1)) / 2;
  return candles.flatMap((candle, index) => {
    if (index < period - 1) return [];
    const weightedTotal = candles
      .slice(index - period + 1, index + 1)
      .reduce(
        (sum, item, windowIndex) => sum + item.close * (windowIndex + 1),
        0
      );
    return [{ time: candle.time, value: weightedTotal / weightSum }];
  });
}

function rsiPoints(candles, period) {
  if (!candles?.length || candles.length <= period || period < 1) return [];
  let gainSum = 0;
  let lossSum = 0;
  for (let index = 1; index <= period; index += 1) {
    const change = candles[index].close - candles[index - 1].close;
    gainSum += Math.max(change, 0);
    lossSum += Math.max(-change, 0);
  }
  let averageGain = gainSum / period;
  let averageLoss = lossSum / period;
  const rsiData = [];
  for (let index = period; index < candles.length; index += 1) {
    if (index > period) {
      const change = candles[index].close - candles[index - 1].close;
      const gain = Math.max(change, 0);
      const loss = Math.max(-change, 0);
      averageGain = (averageGain * (period - 1) + gain) / period;
      averageLoss = (averageLoss * (period - 1) + loss) / period;
    }
    const rs = averageLoss === 0 ? 100 : averageGain / averageLoss;
    const rsi = 100 - 100 / (1 + rs);
    rsiData.push({
      time: candles[index].time,
      value: Number.isFinite(rsi) ? rsi : 50,
    });
  }
  return rsiData;
}

export function createScriptHelpers() {
  return {
    sma: smaPoints,
    ema: emaPoints,
    wma: wmaPoints,
    rsi: rsiPoints,
    /** Last N closes as numbers */
    closes: (candles, n = null) => {
      const arr = candles.map((c) => c.close);
      return n != null ? arr.slice(-n) : arr;
    },
    highs: (candles) => candles.map((c) => c.high),
    lows: (candles) => candles.map((c) => c.low),
    typical: (c) => (c.high + c.low + c.close) / 3,
  };
}

/**
 * @param {string} code — function body; use `return { pane, series }`
 * @param {object[]} candles — OHLC bars (copied before run)
 * @returns {{ ok: true, result } | { ok: false, error: string }}
 */
const MAX_SCRIPT_LENGTH = 60_000;
const MAX_SCRIPT_CANDLES = 50_000;
const SCRIPT_TIMEOUT_MS = 5_000;
let runSequence = 0;

export function runUserScript(code, candles, { signal } = {}) {
  const trimmed = String(code ?? "").trim();
  if (!trimmed) {
    return Promise.resolve({ ok: false, error: "Script is empty" });
  }

  if (!Array.isArray(candles) || !candles.length) {
    return Promise.resolve({ ok: false, error: "No candle data" });
  }
  if (trimmed.length > MAX_SCRIPT_LENGTH) {
    return Promise.resolve({ ok: false, error: "Script must be 60 KB or smaller." });
  }

  return new Promise((resolve) => {
    let worker;
    try {
      worker = new Worker(`${process.env.PUBLIC_URL || ""}/user-script-worker.js`);
    } catch (error) {
      resolve({ ok: false, error: error?.message || "Could not start the isolated script worker." });
      return;
    }
    const id = `chart-script-${++runSequence}`;
    let timer = null;
    let finished = false;
    const finish = (result) => {
      if (finished) return;
      finished = true;
      if (timer !== null) window.clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      worker.terminate();
      resolve(result);
    };
    const onAbort = () => finish({ ok: false, error: "Script run cancelled." });
    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });
    timer = window.setTimeout(() => finish({ ok: false, error: "Script exceeded 5 seconds and was stopped." }), SCRIPT_TIMEOUT_MS);
    worker.onmessage = (event) => {
      const message = event.data || {};
      if (message.id !== id) return;
      if (message.ok && message.result && Array.isArray(message.result.series)) finish({ ok: true, result: message.result });
      else finish({ ok: false, error: String(message.error || "The script returned invalid chart data.") });
    };
    worker.onerror = () => finish({ ok: false, error: "The isolated script worker stopped unexpectedly." });
    try {
      worker.postMessage({
        type: "run",
        id,
        code: trimmed,
        candles: candles.slice(-MAX_SCRIPT_CANDLES).map((bar) => ({
          time: bar.time, open: bar.open, high: bar.high, low: bar.low, close: bar.close, volume: bar.volume,
        })),
      });
    } catch (error) {
      finish({ ok: false, error: error?.message || "Could not send candles to the isolated worker." });
    }
  });
}
